import { useState, useCallback, useRef } from 'react';

const KEY_PREFIX = 'form-draft-';
const DRAFT_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export interface TaskMeta {
  taskId: string;
  taskTitle: string;
  propertyId: string;
}

export interface DraftSnapshot<T> {
  formData: Partial<T>;
  hadMedia: boolean;
  savedAt: number;
  pendingSubmit?: boolean;
  taskMeta?: TaskMeta;
}

export interface PendingDraftInfo {
  draftKey: string;
  taskMeta: TaskMeta;
  savedAt: number;
}

export type SerializeFn<T> = (data: T) => { data: Partial<T>; hadMedia: boolean };

function pruneOldDrafts(): void {
  const now = Date.now();
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(KEY_PREFIX)) {
      try {
        const raw = localStorage.getItem(k);
        if (raw) {
          const parsed = JSON.parse(raw) as DraftSnapshot<unknown>;
          if (now - parsed.savedAt > DRAFT_TTL_MS) {
            keysToRemove.push(k);
          }
        }
      } catch {
        keysToRemove.push(k!);
      }
    }
  }
  keysToRemove.forEach((k) => localStorage.removeItem(k));
}

function loadSnapshot<T>(storageKey: string): DraftSnapshot<T> | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DraftSnapshot<T>;
    if (Date.now() - parsed.savedAt > DRAFT_TTL_MS) {
      localStorage.removeItem(storageKey);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function markDraftPendingSubmit(key: string, taskMeta: TaskMeta): void {
  const storageKey = `${KEY_PREFIX}${key}`;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return;
    const snapshot = JSON.parse(raw) as DraftSnapshot<unknown>;
    localStorage.setItem(
      storageKey,
      JSON.stringify({ ...snapshot, pendingSubmit: true, taskMeta }),
    );
  } catch {
    // ignore
  }
}

export function clearDraftByKey(key: string): void {
  try {
    localStorage.removeItem(`${KEY_PREFIX}${key}`);
  } catch {
    // ignore
  }
}

export function loadDraftSnapshot<T>(key: string): DraftSnapshot<T> | null {
  return loadSnapshot<T>(`${KEY_PREFIX}${key}`);
}

export function scanPendingDrafts(): PendingDraftInfo[] {
  const results: PendingDraftInfo[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(KEY_PREFIX)) {
      try {
        const raw = localStorage.getItem(k);
        if (raw) {
          const parsed = JSON.parse(raw) as DraftSnapshot<unknown>;
          if (parsed.pendingSubmit && parsed.taskMeta) {
            results.push({
              draftKey: k.slice(KEY_PREFIX.length),
              taskMeta: parsed.taskMeta,
              savedAt: parsed.savedAt,
            });
          }
        }
      } catch {
        // skip corrupt entries
      }
    }
  }
  return results;
}

export function useDraft<T>(key: string, serialize?: SerializeFn<T>) {
  const storageKey = `${KEY_PREFIX}${key}`;

  // Load draft exactly once using a ref sentinel
  const snapshotRef = useRef<DraftSnapshot<T> | null | undefined>(undefined);
  if (snapshotRef.current === undefined) {
    pruneOldDrafts();
    snapshotRef.current = loadSnapshot<T>(storageKey);
  }
  const snapshot = snapshotRef.current;

  const [draftSavedAt, setDraftSavedAt] = useState<number | null>(snapshot?.savedAt ?? null);

  // Keep serialize in a ref so save callback never needs it in deps
  const serializeRef = useRef<SerializeFn<T>>(
    serialize ?? ((data: T) => ({ data: data as Partial<T>, hadMedia: false })),
  );
  serializeRef.current =
    serialize ?? ((data: T) => ({ data: data as Partial<T>, hadMedia: false }));

  const save = useCallback(
    (data: T) => {
      try {
        const { data: serialized, hadMedia } = serializeRef.current(data);
        const savedAt = Date.now();
        const snapshot: DraftSnapshot<T> = { formData: serialized, hadMedia, savedAt };
        localStorage.setItem(storageKey, JSON.stringify(snapshot));
        setDraftSavedAt(savedAt);
      } catch (error) {
        console.error('Failed to save draft:', error);
      }
    },
    [storageKey],
  );

  const clear = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
      setDraftSavedAt(null);
    } catch (error) {
      console.error('Failed to clear draft:', error);
    }
  }, [storageKey]);

  return { snapshot, draftSavedAt, save, clear };
}
